import React from 'react';
import {
  ArrayField,
  BooleanField,
  ChipField,
  CloneButton,
  DatagridConfigurable,
  DateField,
  ImageField,
  List,
  ReferenceField,
  SingleFieldList,
  TextField,
  EditButton,
  DeleteWithConfirmButton,
  TopToolbar,
  SelectColumnsButton,
  CreateButton,
  FunctionField,
  WrapperField,
  FilterButton,
  SearchInput,
  DateInput,
  BooleanInput,
  CheckboxGroupInput,
  useRecordContext,
  useGetIdentity,
  usePermissions,
} from 'react-admin';

import Stack from '@mui/material/Stack';
import { Tooltip, Typography } from '@mui/material';
import { EventStatus } from '../../types';
import { EVENT_STATUSES } from '../../constants';
import { rolePermission } from '../../utils';

const STATUS_TO_EMOJI_MAP: Record<EventStatus, string> = {
  Approved: '✅',
  WaitingForReview: '⏸',
  WaitingForRefinement: '⏸',
  Rejected: '🚫',
  Cancelled: '❌',
};

const eventFilters = [
  <SearchInput source="q" alwaysOn key="q" />,
  <CheckboxGroupInput
    source="status"
    choices={EVENT_STATUSES.map((status) => ({ id: status, name: status }))}
    key="roles"
  />,
  <DateInput source="createDate" key="dateCreated" />,
  <DateInput source="startDate" key="startDate" />,
  <DateInput source="endDate" key="endDate" />,
  <BooleanInput
    label="Is reported"
    source="reports"
    size="medium"
    key="isReported"
  />,
];

export const EventsList = () => {
  return (
    <List
      filters={eventFilters}
      actions={
        <TopToolbar>
          <CreateButton />
          <FilterButton />
          <SelectColumnsButton />
        </TopToolbar>
      }
    >
      <EventsListDatagrid showUserRef={true} />
    </List>
  );
};

const EventShow = () => {
  return (
    <Stack spacing={1}>
      <ImageField
        source="pictureLink"
        label="Image"
        sx={{ '&>img': { maxWidth: '150px' } }}
      />

      <Stack>
        <Typography variant="caption">Description:</Typography>
        <TextField source="description" label="Description" />
      </Stack>

      <Stack>
        <Typography variant="caption" gutterBottom>
          Categories:
        </Typography>
        <ArrayField source="categories" label="Categories">
          <SingleFieldList linkType={false}>
            <ChipField source="name" />
          </SingleFieldList>
        </ArrayField>
      </Stack>
    </Stack>
  );
};

const RowActions = () => {
  const record = useRecordContext();
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions();

  const isEditPermitted = rolePermission.eventsEditDelete(permissions);
  const isEventCreatedByCurrentUser = record?.userCreated?.id === identity?.id;

  return (
    <Stack spacing={1} alignItems="flex-start">
      <CloneButton />
      {/* we don't forbid Edit, because it has approve/reject logic that should be available to all roles */}
      <EditButton />
      {isEditPermitted || isEventCreatedByCurrentUser ? (
        <DeleteWithConfirmButton />
      ) : null}
    </Stack>
  );
};

export const EventsListDatagrid = (props: { showUserRef?: boolean }) => {
  return (
    <List sort={{field: 'dateCreated', order: 'DESC'}}>
    <DatagridConfigurable rowClick="expand" expand={<EventShow />} >
      <TextField source="name" label="Event Name" />
      <DateField source="dateCreated" label="Created" />

      {props.showUserRef ? (
        <ReferenceField
          label="Created By"
          source="userCreated.id"
          reference="/users"
          sortBy="firstName"
        >
          <TextField source="firstName" /> <TextField source="lastName" />
        </ReferenceField>
      ) : null}

      <TextField source="organizer" label="Organizer" />
      <TextField source="address" label="Address" />
      <TextField source="countryId" label="Country" />
      <DateField source="startDate" label="Starts" />
      <DateField source="endDate" label="Ends" />
      <FunctionField
        source="status"
        label="Status"
        render={(record: any) => (
          <Tooltip title={record.status}>
            <Typography variant="body1" fontSize={18} textAlign="center">
              {STATUS_TO_EMOJI_MAP[record.status as EventStatus]}
            </Typography>
          </Tooltip>
        )}
      />
      <BooleanField
        source="isReported"
        sortBy="totalReports"
        label="Reported"
      />

      <WrapperField label="Actions">
        <RowActions />
      </WrapperField>
    </DatagridConfigurable>
    </List>
  );
};
